/**
 * Sentry browser bootstrap (ai-avenues / cb16-frontend).
 *
 * Isolated module: no business logic lives here. Initialisation is idempotent
 * and browser-only so SSR / Worker rendering is unaffected.
 */
import * as Sentry from "@sentry/react";

/** DSN comes from the environment only — never hardcoded in source. */
export const SENTRY_DSN = import.meta.env["VITE_SENTRY_DSN"] ?? "";

/** Release tracking — injected at build time, falls back to "development". */
export const SENTRY_RELEASE = import.meta.env["VITE_SENTRY_RELEASE"] ?? "cb16-frontend@development";

export const SENTRY_ENVIRONMENT =
  import.meta.env["VITE_SENTRY_ENVIRONMENT"] ?? (import.meta.env.DEV ? "development" : "production");

let started = false;

export function initSentry(router?: unknown) {
  if (started || typeof window === "undefined" || !SENTRY_DSN) return;
  started = true;

  Sentry.init({
    dsn: SENTRY_DSN,
    release: SENTRY_RELEASE,
    environment: SENTRY_ENVIRONMENT,
    integrations: [
      // Browser performance tracing (page loads, navigations, fetch/XHR spans).
      // With a router instance we get parameterised route-navigation traces.
      router
        ? Sentry.tanstackRouterBrowserTracingIntegration(
            router as Parameters<typeof Sentry.tanstackRouterBrowserTracingIntegration>[0],
          )
        : Sentry.browserTracingIntegration(),
      // Session Replay.
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    // Automatic JS error + unhandled rejection reporting is on by default.
    tracesSampleRate: import.meta.env.DEV ? 1.0 : 0.2,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    // Every event carries these alongside environment + release.
    initialScope: {
      tags: { application: "cb16", component: "frontend" },
    },
  });

  installFetchFailureCapture();
}

/**
 * Captures failed network calls (fetch transport errors and non-OK HTTP
 * responses, including Supabase REST/Auth/Storage endpoints) as Sentry events.
 * Purely observational — the original response/error is always passed through.
 */
function installFetchFailureCapture() {
  const w = window as Window & { __cb16SentryFetchPatched?: boolean };
  if (w.__cb16SentryFetchPatched) return;
  w.__cb16SentryFetchPatched = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const method = (init?.method ?? (input instanceof Request ? input.method : "GET")).toUpperCase();
    const url =
      typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;

    try {
      const response = await originalFetch(input as RequestInfo, init);
      if (!response.ok) {
        const supabase = /supabase\.co|\.lovable\.cloud/.test(url);
        Sentry.captureMessage(`${supabase ? "Supabase" : "HTTP"} request failed: ${method} ${strip(url)}`, {
          level: response.status >= 500 ? "error" : "warning",
          tags: { kind: supabase ? "supabase_request" : "http_request", status: String(response.status) },
          extra: { url: strip(url), method, status: response.status, statusText: response.statusText },
        });
      }
      return response;
    } catch (error) {
      Sentry.captureException(error, {
        tags: { kind: "fetch_network_error" },
        extra: { url: strip(url), method },
      });
      throw error;
    }
  };
}

/** Drops query strings so tokens in URLs never reach Sentry. */
function strip(url: string) {
  const i = url.indexOf("?");
  return i === -1 ? url : url.slice(0, i);
}
